/**
 * Shared zod helpers for the route search schemas (`pages/**\/*.search.ts`).
 *
 * TanStack Router parses every search value with JSON first, so a hand-typed
 * `?id=123` arrives as the number 123 and `?q=true` as the boolean true. Each
 * helper coerces those back to what the page means, and every one is
 * `.optional().catch(undefined)`: a malformed or stale bookmark falls back to
 * the page's default instead of throwing a search-param error.
 *
 * Schema files import only zod and these helpers, never page modules, so the
 * router can import them without pulling the lazy page chunks in. (The table
 * page sizes come from a constants-only module, for the same reason.)
 */
import { z } from 'zod';
import { TABLE_PAGE_SIZES } from '../components/inputs/PaginatedDataTable/pageSizes';

const scalar = z.union([z.string(), z.number(), z.boolean(), z.null()]);

/** A scalar search value as the text the user typed (`?q=null` means the word). */
const asText = scalar.transform((v) => String(v));

/** A string; numbers and booleans are coerced back to text, empty means unset. */
export const str = asText.pipe(z.string().min(1)).optional().catch(undefined);

/** A whole number of at least 1 (`?credits=25000`). */
export const positiveInt = z
  .union([z.string(), z.number()])
  .transform((v) => Number(v))
  .pipe(z.number().int().min(1))
  .optional()
  .catch(undefined);

/** A 1-based page: a whole number of at least 1. (A page size is `pageSize`.) */
export const pageNum = positiveInt;

/** A yes/no switch: `true`/`1` or `false`/`0`, as JSON or as text. */
export const flag = z
  .union([z.boolean(), z.number(), z.string()])
  .transform((v, ctx) => {
    if (v === true || v === 1 || v === 'true' || v === '1') return true;
    if (v === false || v === 0 || v === 'false' || v === '0') return false;
    ctx.addIssue({ code: 'custom', message: 'not a flag' });
    return z.NEVER;
  })
  .optional()
  .catch(undefined);

/**
 * One of a fixed set of text values. The JSON-parsed number or boolean form of
 * a member is accepted too (`?forbidden=1` for `'1'`).
 */
export function oneOf<const T extends readonly [string, ...string[]]>(values: T) {
  return asText.pipe(z.literal(values)).optional().catch(undefined);
}

/** One of a fixed set of numbers (`?days=7`); the quoted text form is accepted too. */
export function oneOfNumbers<const T extends readonly [number, ...number[]]>(values: T) {
  return z
    .union([z.string(), z.number()])
    .transform((v) => Number(v))
    .pipe(z.literal(values))
    .optional()
    .catch(undefined);
}

/**
 * A table page size: one of the sizes a paginated table offers, so an edited
 * link can't ask the server for any number of rows. Typed as a plain number, as the
 * tables report a size change.
 */
export const pageSize = z
  .union([z.string(), z.number()])
  .transform((v) => Number(v))
  .refine((v) => (TABLE_PAGE_SIZES as readonly number[]).includes(v))
  .optional()
  .catch(undefined);

const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

/** A real calendar day as `YYYY-MM-DD` (30 February is refused). */
export const isoDate = z
  .string()
  .refine((v) => {
    const m = ISO_DAY.exec(v);
    if (!m) return false;
    const ms = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return new Date(ms).toISOString().slice(0, 10) === v;
  })
  .optional()
  .catch(undefined);

/**
 * A comma-separated list (`?expanded=a,b`), trimmed, empties dropped. Also
 * accepts the JSON array the router writes for an array value. Write it back
 * joined (`list.join(',')`) so the address stays readable.
 */
export const csv = z
  .union([asText, z.array(asText)])
  .transform((v) => (Array.isArray(v) ? v : v.split(',')).map((s) => s.trim()).filter(Boolean))
  .pipe(z.array(z.string()).min(1))
  .optional()
  .catch(undefined);
